import { useState, useRef, useEffect, useCallback, type ReactNode } from "react";
import { motion } from "framer-motion";
import {
  Check,
  ChevronDown,
  type LucideIcon,
} from "lucide-react";

export interface ProcessStep {
  title: string;
  description: string;
  preview: ReactNode;
}

interface HowItWorksProps {
  steps: ProcessStep[];
  heading: string;
  subtitle: string;
  trustText: string;
  t: (key: string) => string;
}

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

/* shared child animation — used by all building blocks */
const item = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
};
const itemTransition = (d: number) => ({
  duration: 0.35,
  delay: d,
  ease: [0.25, 0.1, 0.25, 1] as const,
});

/* ------------------------------------------------------------------ */
/* Reusable illustration building blocks                               */
/* ------------------------------------------------------------------ */

export function StatusRow({
  label,
  status,
  connected = false,
  icon: Icon,
  logo,
  delay = 0,
}: {
  label: string;
  status: string;
  connected?: boolean;
  icon?: LucideIcon;
  logo?: string;
  delay?: number;
}) {
  return (
    <motion.div
      {...item}
      transition={itemTransition(delay)}
      className="flex items-center justify-between gap-3 bg-card rounded-xl px-4 py-3 shadow-[0_1px_2px_rgba(10,13,20,0.03),0_0_0_1px_rgba(23,23,23,0.04)]"
    >
      <div className="flex items-center gap-2.5">
        {logo ? (
          <img src={logo} alt="" className="h-4 w-4 object-contain shrink-0" />
        ) : Icon ? (
          <Icon className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
        ) : (
          <span
            className={`w-2 h-2 rounded-full shrink-0 ${connected ? "bg-emerald-500" : "bg-border"}`}
          />
        )}
        <span className="text-sm font-medium text-foreground">{label}</span>
      </div>
      <span
        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
          connected
            ? "bg-emerald-50 text-emerald-600"
            : "bg-secondary text-muted-foreground"
        }`}
      >
        {status}
      </span>
    </motion.div>
  );
}

export function MetricCard({
  label,
  value,
  trend,
  icon: Icon,
  delay = 0,
}: {
  label: string;
  value: string;
  trend?: "up" | "down";
  icon?: LucideIcon;
  delay?: number;
}) {
  return (
    <motion.div
      {...item}
      transition={itemTransition(delay)}
      className="bg-card rounded-xl px-4 py-3 shadow-[0_1px_2px_rgba(10,13,20,0.03),0_0_0_1px_rgba(23,23,23,0.04)]"
    >
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />}
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-lg font-semibold text-foreground">{value}</span>
        {trend && (
          <span
            className={`text-xs font-medium ${trend === "up" ? "text-emerald-500" : "text-rose-500"}`}
          >
            {trend === "up" ? "\u2191" : "\u2193"}
          </span>
        )}
      </div>
    </motion.div>
  );
}

export function CheckItem({
  label,
  checked = false,
  delay = 0,
  tickDelay,
}: {
  label: string;
  checked?: boolean;
  delay?: number;
  /** When provided, the item starts unchecked and animates to checked after this delay (s). */
  tickDelay?: number;
}) {
  const animatesLive = typeof tickDelay === "number";
  const finalChecked = animatesLive ? true : checked;

  return (
    <motion.div
      {...item}
      transition={itemTransition(delay)}
      className="flex items-center gap-2.5"
    >
      <motion.span
        initial={{
          backgroundColor: animatesLive
            ? "hsl(var(--background))"
            : finalChecked
            ? "hsl(var(--primary))"
            : "hsl(var(--background))",
          borderColor: "hsl(var(--border))",
        }}
        animate={{
          backgroundColor: finalChecked
            ? "hsl(var(--primary))"
            : "hsl(var(--background))",
          borderColor: finalChecked
            ? "hsl(var(--primary))"
            : "hsl(var(--border))",
        }}
        transition={{
          duration: 0.25,
          delay: animatesLive ? tickDelay : 0,
          ease: [0.25, 0.1, 0.25, 1],
        }}
        className="w-4 h-4 rounded border shrink-0 flex items-center justify-center relative overflow-hidden"
      >
        {finalChecked && (
          <motion.svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-2.5 w-2.5 text-white"
            initial={{ pathLength: animatesLive ? 0 : 1, opacity: animatesLive ? 0 : 1 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{
              pathLength: {
                duration: 0.3,
                delay: animatesLive ? (tickDelay ?? 0) + 0.15 : 0,
                ease: [0.65, 0, 0.35, 1],
              },
              opacity: {
                duration: 0.1,
                delay: animatesLive ? (tickDelay ?? 0) + 0.15 : 0,
              },
            }}
          >
            <motion.path d="M3 8.5l3.2 3.2L13 5" />
          </motion.svg>
        )}
      </motion.span>
      <motion.span
        className="text-sm"
        initial={{
          color: animatesLive
            ? "hsl(var(--muted-foreground))"
            : finalChecked
            ? "hsl(var(--foreground))"
            : "hsl(var(--muted-foreground))",
        }}
        animate={{
          color: finalChecked
            ? "hsl(var(--foreground))"
            : "hsl(var(--muted-foreground))",
        }}
        transition={{
          duration: 0.25,
          delay: animatesLive ? (tickDelay ?? 0) + 0.05 : 0,
          ease: [0.25, 0.1, 0.25, 1],
        }}
      >
        {label}
      </motion.span>
    </motion.div>
  );
}

export function FakeSelect({
  label,
  value,
  icon: Icon,
  logo,
  delay = 0,
}: {
  label: string;
  value: string;
  icon?: LucideIcon;
  logo?: string;
  delay?: number;
}) {
  return (
    <motion.div {...item} transition={itemTransition(delay)}>
      <p className="text-xs font-medium text-foreground mb-1">{label}</p>
      <div className="flex items-center justify-between bg-card border border-border rounded-lg px-3 py-2 shadow-[0_1px_2px_rgba(10,13,20,0.03)]">
        <div className="flex items-center gap-2">
          {logo ? (
            <img src={logo} alt="" className="h-3.5 w-3.5 object-contain" />
          ) : Icon ? (
            <Icon className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
          ) : null}
          <span className="text-sm text-foreground">{value}</span>
        </div>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
    </motion.div>
  );
}

export function ProgressBar({
  label,
  percent,
  color = "bg-primary",
  icon: Icon,
  delay = 0,
}: {
  label: string;
  percent: number;
  color?: string;
  icon?: LucideIcon;
  delay?: number;
}) {
  return (
    <motion.div {...item} transition={itemTransition(delay)}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          {Icon && <Icon className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />}
          <span className="text-xs font-medium text-foreground">{label}</span>
        </div>
        <span className="text-xs text-muted-foreground">{percent}%</span>
      </div>
      <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
        <motion.div
          className={`h-full rounded-full ${color}`}
          initial={{ width: 0 }}
          animate={{ width: `${percent}%` }}
          transition={{ duration: 0.8, delay: delay + 0.15, ease: [0.25, 0.1, 0.25, 1] }}
        />
      </div>
    </motion.div>
  );
}

export function Tag({
  children,
  active = false,
  icon: Icon,
  logo,
  delay = 0,
}: {
  children: string;
  active?: boolean;
  icon?: LucideIcon;
  logo?: string;
  delay?: number;
}) {
  return (
    <motion.span
      {...item}
      transition={itemTransition(delay)}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${
        active ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"
      }`}
    >
      {logo ? (
        <img src={logo} alt="" className="h-3 w-3 object-contain" />
      ) : Icon ? (
        <Icon className="h-3 w-3" strokeWidth={1.5} />
      ) : null}
      {children}
    </motion.span>
  );
}

/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */

export function HowItWorks({ steps, heading, subtitle, trustText, t }: HowItWorksProps) {
  const [openStep, setOpenStep] = useState(0);
  const [userClicked, setUserClicked] = useState(false);
  const outerRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const userClickTimer = useRef<ReturnType<typeof setTimeout>>();

  // px of scroll dedicated to each step — gives users time to read each
  const SCROLL_PER_STEP = 350;
  const extraScroll = steps.length * SCROLL_PER_STEP;

  const handleManualClick = useCallback(
    (i: number) => {
      setUserClicked(true);
      setOpenStep((prev) => (prev === i ? -1 : i));
      clearTimeout(userClickTimer.current);
      userClickTimer.current = setTimeout(() => setUserClicked(false), 3000);
    },
    []
  );

  // Scroll-pinned: map scroll progress through the tall outer div to step index
  useEffect(() => {
    const outer = outerRef.current;
    if (!outer) return;

    const onScroll = () => {
      if (userClicked) return;
      const rect = outer.getBoundingClientRect();
      const scrollable = outer.offsetHeight - window.innerHeight;
      if (scrollable <= 0) return;

      // Only map scroll → step while the section is actually pinned
      // (top has reached 0 and bottom hasn't passed viewport yet).
      // This prevents flicker at entry/exit boundaries.
      if (rect.top > 0 || rect.bottom < window.innerHeight) return;

      const scrolled = -rect.top;
      const progress = Math.max(0, Math.min(0.999, scrolled / scrollable));
      const idx = Math.min(steps.length - 1, Math.floor(progress * steps.length));

      setOpenStep((prev) => (prev === idx ? prev : idx));
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [userClicked, steps.length]);

  return (
    // Outer wrapper: tall enough to give each step its own scroll real-estate
    <div ref={outerRef} style={{ minHeight: `calc(100vh + ${extraScroll}px)` }}>
      {/* Sticky inner — pins while user scrolls through */}
      <div ref={stickyRef} className="sticky top-0 py-16 lg:py-24">
        <div className="container px-6">
          {/* Header */}
          <div className="max-w-2xl mx-auto text-center mb-10">
            <motion.span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-secondary text-sm text-muted-foreground font-medium mb-4"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={0}
            >
              {t("How it works")}
            </motion.span>
            <motion.h2
              className="text-3xl sm:text-4xl lg:text-[2.5rem] font-display font-bold leading-tight mb-4"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={1}
            >
              {heading}
            </motion.h2>
            <motion.p
              className="text-base text-muted-foreground leading-relaxed max-w-lg mx-auto text-balance"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={2}
            >
              {subtitle}
            </motion.p>
          </div>

          {/* Accordion steps */}
          <div className="max-w-[700px] mx-auto flex flex-col gap-3">
            {steps.map((step, i) => {
              const isOpen = openStep === i;
              const isCompleted = i < openStep;

              return (
                <motion.div
                  key={step.title}
                  className="rounded-[20px] border border-border/60 bg-card transition-all duration-300 overflow-hidden shadow-[0_3px_3px_-1.5px_rgba(23,23,23,0.04),0_1px_1px_-0.5px_rgba(23,23,23,0.04),0_0_0_1px_rgba(23,23,23,0.06)]"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                >
                  {/* Trigger row */}
                  <button
                    onClick={() => handleManualClick(i)}
                    className="w-full flex items-center gap-5 p-6 lg:p-7 text-left"
                  >
                    {isCompleted ? (
                      <motion.span
                        className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0"
                        initial={{ scale: 0.8 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        key={`check-${i}`}
                      >
                        <Check className="h-5 w-5 text-primary" />
                      </motion.span>
                    ) : (
                      <span
                        className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-medium shrink-0 transition-all duration-300 ${
                          isOpen
                            ? "border-2 border-primary/20 text-primary"
                            : "bg-secondary text-muted-foreground"
                        }`}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    )}

                    <div className="flex-1 min-w-0">
                      <p
                        className={`font-sans font-medium text-base transition-colors duration-200 ${
                          isOpen || isCompleted
                            ? "text-foreground"
                            : "text-muted-foreground"
                        }`}
                      >
                        {t(step.title)}
                      </p>
                      <p
                        className={`text-sm mt-0.5 text-balance transition-colors duration-200 ${
                          isOpen
                            ? "text-muted-foreground"
                            : "text-muted-foreground/60"
                        }`}
                      >
                        {t(step.description)}
                      </p>
                    </div>

                    <span
                      className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 transition-all duration-200 ${
                        isOpen
                          ? "border-primary/20 bg-primary/5"
                          : "border-border bg-card shadow-sm"
                      }`}
                    >
                      <motion.div
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                      >
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      </motion.div>
                    </span>
                  </button>

                  {/* Expanded preview — always mounted, animates height/opacity to avoid remount flashing during scroll */}
                  <motion.div
                    initial={false}
                    animate={{
                      height: isOpen ? "auto" : 0,
                      opacity: isOpen ? 1 : 0,
                    }}
                    transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                    style={{ overflow: "hidden" }}
                  >
                    <div className="px-6 lg:px-7 pb-6 lg:pb-7">
                      <div className="bg-secondary/60 rounded-2xl p-4 lg:p-5 relative overflow-hidden min-h-[180px]">
                        {step.preview}
                        <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-secondary/60 to-transparent pointer-events-none" />
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>

          {/* Trust section removed */}
        </div>
      </div>
    </div>
  );
}
